/**
 * Location Detection Module
 * Handles automatic country and region detection using IP geolocation
 * Supports USA (2 regions) and Canada (5 regions)
 */

class LocationDetection {
  constructor() {
    this.CACHE_KEY = 'inspiredgo_location';
    this.FAIL_CACHE_KEY = 'inspiredgo_location_fail';
    this.FAIL_CACHE_TTL = 5 * 60 * 1000; // 5 minutes — avoid retry storms on API failure
    this.settings = this.loadSettings();
    this.regionMappings = this.initializeRegionMappings();
    this._pendingDetection = null; // dedup concurrent getLocation() calls
  }

  /**
   * Load settings from Shopify theme settings
   */
  loadSettings() {
    return {
      apiKey: window.themeSettings?.location_api_key || '',
      enabled: window.themeSettings?.location_detection_enabled !== false,
      autoRoute: window.themeSettings?.location_auto_route !== false,
      showBanner: window.themeSettings?.location_show_banner !== false,
      confirmCountrySwitch: window.themeSettings?.location_confirm_country_switch !== false,
      cacheDays: parseInt(window.themeSettings?.location_cache_days) || 7,
      labels: {
        us_west: window.themeSettings?.region_label_us_west || 'West Coast',
        us_east: window.themeSettings?.region_label_us_east || 'East Coast',
        ca_bc: window.themeSettings?.region_label_ca_bc || 'BC',
        ca_ab: window.themeSettings?.region_label_ca_ab || 'AB',
        ca_sk: window.themeSettings?.region_label_ca_sk || 'SK',
        ca_mb: window.themeSettings?.region_label_ca_mb || 'MB',
        ca_on: window.themeSettings?.region_label_ca_on || 'ON',
        ca_qc: window.themeSettings?.region_label_ca_qc || 'QC',
        us_west_mobile: window.themeSettings?.region_label_us_west_mobile || 'West',
        us_east_mobile: window.themeSettings?.region_label_us_east_mobile || 'East',
        ca_bc_mobile: window.themeSettings?.region_label_ca_bc_mobile || 'BC',
        ca_ab_mobile: window.themeSettings?.region_label_ca_ab_mobile || 'AB',
        ca_sk_mobile: window.themeSettings?.region_label_ca_sk_mobile || 'SK',
        ca_mb_mobile: window.themeSettings?.region_label_ca_mb_mobile || 'MB',
        ca_on_mobile: window.themeSettings?.region_label_ca_on_mobile || 'ON',
        ca_qc_mobile: window.themeSettings?.region_label_ca_qc_mobile || 'QC'
      }
    };
  }

  /**
   * Initialize region mapping configurations
   */
  initializeRegionMappings() {
    return {
      usa: {
        west: ['CA', 'NV', 'UT', 'AZ', 'CO'],
        east: ['NY', 'NJ', 'PA', 'DC']
      },
      canada: {
        bc: ['BC'],
        ab: ['AB'],
        sk: ['SK'],
        mb: ['MB'],
        on: ['ON'],
        qc: ['QC']
      }
    };
  }

  /**
   * Get cached location data from sessionStorage
   * @returns {Object|null} Cached location data or null if not found
   */
  getCachedLocation() {
    try {
      const cached = sessionStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);

      return data.location;
    } catch (error) {
      console.error('Error reading cached location:', error);
      return null;
    }
  }

  /**
   * Cache location data to sessionStorage
   * @param {Object} locationData - Location data to cache
   */
  cacheLocation(locationData) {
    try {
      const cacheData = {
        location: locationData,
        cachedAt: new Date().toISOString()
      };

      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching location:', error);
    }
  }

  /**
   * Cache a detection failure so we don't retry on every page view.
   * Stored separately with a short TTL (5 min).
   */
  cacheFailure(errorMessage) {
    try {
      sessionStorage.setItem(this.FAIL_CACHE_KEY, JSON.stringify({
        error: errorMessage,
        cachedAt: Date.now()
      }));
    } catch (e) {
      // ignore
    }
  }

  /**
   * Check if a recent failure is cached (within TTL).
   * @returns {string|null} Error message if within TTL, null otherwise
   */
  getCachedFailure() {
    try {
      const raw = sessionStorage.getItem(this.FAIL_CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.cachedAt < this.FAIL_CACHE_TTL) {
        return data.error;
      }
      // expired
      sessionStorage.removeItem(this.FAIL_CACHE_KEY);
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clear cached location data
   */
  clearCache() {
    try {
      sessionStorage.removeItem(this.CACHE_KEY);
      sessionStorage.removeItem(this.FAIL_CACHE_KEY);
    } catch (error) {
      console.error('Error clearing location cache:', error);
    }
  }

  /**
   * Map US state to region
   * @param {string} state - Two-letter state code (e.g., 'CA', 'NY') or full state name
   * @returns {Object} Region info { code: 'us_west', label: 'West Coast', confidence: 'high' }
   */
  mapUSStateToRegion(state) {
    if (!state) {
      return { code: null, label: null, confidence: 'none' };
    }

    // Map of full state names to abbreviations
    const stateNameToCode = {
      'CALIFORNIA': 'CA',
      'NEVADA': 'NV',
      'UTAH': 'UT',
      'ARIZONA': 'AZ',
      'COLORADO': 'CO',
      'NEW YORK': 'NY',
      'NEW JERSEY': 'NJ',
      'PENNSYLVANIA': 'PA',
      'DISTRICT OF COLUMBIA': 'DC',
      'WASHINGTON DC': 'DC',
      'WASHINGTON D.C.': 'DC'
    };

    let stateCode = state.toUpperCase();

    // If it's a full state name, convert to code
    if (stateCode.length > 2) {
      stateCode = stateNameToCode[stateCode] || stateCode;
    }

    if (this.regionMappings.usa.west.includes(stateCode)) {
      return {
        code: 'us_west',
        label: this.settings.labels.us_west,
        labelMobile: this.settings.labels.us_west_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.usa.east.includes(stateCode)) {
      return {
        code: 'us_east',
        label: this.settings.labels.us_east,
        labelMobile: this.settings.labels.us_east_mobile,
        confidence: 'high'
      };
    }

    return {
      code: null,
      label: null,
      confidence: 'none',
      error: 'State not in serviced regions'
    };
  }

  /**
   * Map Canadian province to region
   * @param {string} province - Two-letter province code (e.g., 'BC', 'ON') or full province name
   * @returns {Object} Region info { code: 'ca_bc', label: 'BC', confidence: 'high' }
   */
  mapCanadaProvinceToRegion(province) {
    if (!province) {
      return { code: null, label: null, confidence: 'none' };
    }

    // Map of full province names to abbreviations
    const provinceNameToCode = {
      'BRITISH COLUMBIA': 'BC',
      'ALBERTA': 'AB',
      'SASKATCHEWAN': 'SK',
      'MANITOBA': 'MB',
      'ONTARIO': 'ON',
      'QUEBEC': 'QC',
      'QUÉBEC': 'QC'
    };

    let provinceCode = province.toUpperCase();

    // If it's a full province name, convert to code
    if (provinceCode.length > 2) {
      provinceCode = provinceNameToCode[provinceCode] || provinceCode;
    }

    if (this.regionMappings.canada.bc.includes(provinceCode)) {
      return {
        code: 'ca_bc',
        label: this.settings.labels.ca_bc,
        labelMobile: this.settings.labels.ca_bc_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.canada.ab.includes(provinceCode)) {
      return {
        code: 'ca_ab',
        label: this.settings.labels.ca_ab,
        labelMobile: this.settings.labels.ca_ab_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.canada.sk.includes(provinceCode)) {
      return {
        code: 'ca_sk',
        label: this.settings.labels.ca_sk,
        labelMobile: this.settings.labels.ca_sk_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.canada.mb.includes(provinceCode)) {
      return {
        code: 'ca_mb',
        label: this.settings.labels.ca_mb,
        labelMobile: this.settings.labels.ca_mb_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.canada.on.includes(provinceCode)) {
      return {
        code: 'ca_on',
        label: this.settings.labels.ca_on,
        labelMobile: this.settings.labels.ca_on_mobile,
        confidence: 'high'
      };
    }

    if (this.regionMappings.canada.qc.includes(provinceCode)) {
      return {
        code: 'ca_qc',
        label: this.settings.labels.ca_qc,
        labelMobile: this.settings.labels.ca_qc_mobile,
        confidence: 'high'
      };
    }

    return {
      code: null,
      label: null,
      confidence: 'none',
      error: 'Province not in serviced regions'
    };
  }

  /**
   * Detect location using IPinfo.io API
   * @returns {Promise<Object>} Location data with country, region, confidence
   */
  async detectLocation() {
    if (!this.settings.enabled) {
      return {
        success: false,
        error: 'Location detection is disabled'
      };
    }

    if (!this.settings.apiKey) {
      console.error('IPinfo API key not configured!');
      return {
        success: false,
        error: 'IPinfo.io API key not configured'
      };
    }

    try {
      const response = await fetch(`https://ipinfo.io/json?token=${this.settings.apiKey}`);

      if (!response.ok) {
        const statusCode = response.status;
        const errorMsg = statusCode === 429
          ? 'IPinfo API rate limit exceeded'
          : `IPinfo API error: ${statusCode}`;
        console.warn(errorMsg);
        return {
          success: false,
          error: errorMsg,
          rateLimited: statusCode === 429,
          confidence: 'none'
        };
      }

      const data = await response.json();

      // Extract country and region/state
      const country = data.country; // 'US' or 'CA'
      const region = data.region; // Full state/province name (e.g., 'British Columbia')

      let locationData = {
        success: true,
        country: country,
        countryName: country === 'US' ? 'United States' : country === 'CA' ? 'Canada' : country,
        rawRegion: region,
        ipData: {
          city: data.city,
          postal: data.postal,
          timezone: data.timezone
        }
      };

      // Map to our service regions
      if (country === 'US') {
        const regionInfo = this.mapUSStateToRegion(region);
        locationData = {
          ...locationData,
          ...regionInfo
        };
      } else if (country === 'CA') {
        const regionInfo = this.mapCanadaProvinceToRegion(region);
        locationData = {
          ...locationData,
          ...regionInfo
        };
      } else {
        locationData.confidence = 'none';
        locationData.error = 'Country not serviced';
      }

      return locationData;
    } catch (error) {
      console.error('Location detection error:', error);
      return {
        success: false,
        error: error.message,
        confidence: 'none'
      };
    }
  }

  /**
   * Get location with caching and deduplication.
   *
   * - Returns cached success if available
   * - Returns early if a recent failure is cached (avoids retry storms)
   * - Deduplicates concurrent calls so only ONE API request fires per page load
   *
   * @param {boolean} forceRefresh - Skip cache and force new detection
   * @returns {Promise<Object>} Location data
   */
  async getLocation(forceRefresh = false) {
    if (!forceRefresh) {
      // 1. Check successful detection cache
      const cached = this.getCachedLocation();
      if (cached) {
        return {
          ...cached,
          fromCache: true
        };
      }

      // 2. Check failure cache — don't re-hit a rate-limited API on every page view
      const failError = this.getCachedFailure();
      if (failError) {
        return {
          success: false,
          error: failError,
          confidence: 'none',
          fromCache: true
        };
      }
    }

    // 3. Deduplicate: if a detection is already in-flight, reuse its promise
    if (this._pendingDetection) {
      return this._pendingDetection;
    }

    this._pendingDetection = this._doDetectAndCache();

    try {
      return await this._pendingDetection;
    } finally {
      this._pendingDetection = null;
    }
  }

  /**
   * Internal: run detection and handle caching.
   * Separated so _pendingDetection can hold the single in-flight promise.
   */
  async _doDetectAndCache() {
    const detected = await this.detectLocation();

    if (detected.success) {
      // If the user manually selected a location while the API was in-flight,
      // do not overwrite their selection with the auto-detected result.
      const existing = this.getCachedLocation();
      if (existing && existing.confidence === 'manual') {
        return { ...existing, fromCache: false };
      }
      this.cacheLocation(detected);
    } else {
      // Cache the failure for a short window so we don't hammer the API
      this.cacheFailure(detected.error || 'Unknown detection error');
    }

    return {
      ...detected,
      fromCache: false
    };
  }

  /**
   * Manually set location (for user override)
   * @param {string} country - 'US' or 'CA'
   * @param {string} regionCode - Region code (e.g., 'us_west', 'ca_bc')
   */
  setLocation(country, regionCode) {
    const locationData = {
      success: true,
      country: country,
      countryName: country === 'US' ? 'United States' : 'Canada',
      code: regionCode,
      confidence: 'manual',
      manuallySet: true,
      setAt: new Date().toISOString()
    };

    // Add label based on region code
    if (regionCode && this.settings.labels[regionCode]) {
      locationData.label = this.settings.labels[regionCode];
      locationData.labelMobile = this.settings.labels[`${regionCode}_mobile`];
    }

    // Clear any failure cache when user manually sets location
    try { sessionStorage.removeItem(this.FAIL_CACHE_KEY); } catch (e) { /* ignore */ }

    this.cacheLocation(locationData);
    return locationData;
  }

  /**
   * Sync the saved `inspiredgo_location` from a zip-resolved region name.
   * Used to keep the IP-detected/manually-picked region in lockstep with the
   * zip the user has confirmed in cart-sidebar / zcp-popup / zip-code-checker,
   * so the menu-vs-saved-region mismatch popup (switchConfirmModal) doesn't fire.
   *
   * Only overwrites the saved region if the existing region maps to a
   * *different* menu — preserves the user's specific province choice when it
   * still leads to the same menu.
   *
   * @param {string} resolvedRegion - 'toronto' | 'montreal' | 'calgary' | 'USA-East' | 'USA-West'
   */
  syncRegionFromZip(resolvedRegion) {
    if (!resolvedRegion) return;
    const targets = {
      'toronto':  { country: 'CA', region: 'ca_on', menuPeers: ['ca_on', 'ca_east'] },
      'quebec':   { country: 'CA', region: 'ca_qc', menuPeers: ['ca_qc'] },
      'calgary':  { country: 'CA', region: 'ca_ab', menuPeers: ['ca_bc', 'ca_ab', 'ca_sk', 'ca_mb', 'ca_west'] },
      'USA-East': { country: 'US', region: 'us_east', menuPeers: ['us_east'] },
      'USA-West': { country: 'US', region: 'us_west', menuPeers: ['us_west'] }
    };
    const target = targets[resolvedRegion];
    if (!target) return;

    // Peek at cached value synchronously (don't trigger IP detection)
    let currentCode = null;
    try {
      const raw = sessionStorage.getItem(this.CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        currentCode = parsed && parsed.code;
      }
    } catch (e) {}

    // Already on a region that maps to the same menu — preserve user's specific choice
    if (currentCode && target.menuPeers.indexOf(currentCode) !== -1) return;

    this.setLocation(target.country, target.region);
  }

  /**
   * Get region label for display
   * @param {string} regionCode - Region code (e.g., 'us_west', 'ca_bc')
   * @param {boolean} mobile - Whether to use mobile label
   * @returns {string} Display label
   */
  getRegionLabel(regionCode, mobile = false) {
    if (!regionCode) return '';

    const suffix = mobile ? '_mobile' : '';
    return this.settings.labels[`${regionCode}${suffix}`] || regionCode;
  }

  /**
   * Format location for display
   * @param {Object} location - Location data
   * @param {boolean} mobile - Whether to use mobile format
   * @returns {string} Formatted string like "US · West Coast" or "CA · BC"
   */
  formatLocationDisplay(location, mobile = false) {
    if (!location || !location.country) return '';

    const country = location.country;
    const label = location.label || this.getRegionLabel(location.code, mobile);

    if (!label) return country;

    return `${country} · ${label}`;
  }
}

// Initialize and expose globally
window.LocationDetection = LocationDetection;
window.locationDetector = new LocationDetection();

// Process cross-domain region handoff via URL query params
(function() {
  try {
    const params = new URLSearchParams(window.location.search);
    const region = params.get('region');
    const country = params.get('country');
    if (region && country && window.locationDetector) {
      console.log('Cross-domain handoff: setting location from URL params', country, region);
      window.locationDetector.setLocation(country, region);
      // Clean URL - remove region/country params
      params.delete('region');
      params.delete('country');
      const newUrl = params.toString()
        ? window.location.pathname + '?' + params.toString()
        : window.location.pathname;
      history.replaceState(null, '', newUrl);
    }
  } catch (e) {
    console.error('Error processing cross-domain region params:', e);
  }
})();

// Auto-detect location on every page load and save to sessionStorage
// Runs independently of any UI elements
window.locationDetector.getLocation().then(function(location) {
  console.log('Auto-detection complete:', location.fromCache ? '(from cache)' : '(fresh)', location);
}).catch(function(err) {
  console.error('Auto-detection failed:', err);
});

// Keep `inspiredgo_location` in sync with the confirmed cart zip.
// Any surface that saves `cs_zip` + `cs_location_id` to sessionStorage and
// dispatches 'inspiredgo:zip-updated' will also have the IP/manual region
// updated here, preventing stale-region popups (e.g., switchConfirmModal)
// after a zip-driven menu redirect.
(function() {
  const REGION_LOCATION_IDS = {
    'toronto':  [242677, 270213, 285794, 289774],
    'quebec':   [319959],
    'USA-West': [273216, 276649, 277960, 278438, 279269, 288292, 299051, 301750],
    'USA-East': [301948, 310459, 310466]
  };
  function resolveZipRegion(locationId) {
    const id = Number(locationId);
    if (!id || isNaN(id)) return null;
    const keys = Object.keys(REGION_LOCATION_IDS);
    for (let i = 0; i < keys.length; i++) {
      if (REGION_LOCATION_IDS[keys[i]].indexOf(id) !== -1) return keys[i];
    }
    return 'calgary'; // fallback matches cart-sidebar / zcp-popup behavior
  }
  window.addEventListener('inspiredgo:zip-updated', function() {
    try {
      const locId = sessionStorage.getItem('cs_location_id');
      if (!locId) return; // zip was cleared — leave saved region as-is
      const region = resolveZipRegion(locId);
      if (region && window.locationDetector) {
        window.locationDetector.syncRegionFromZip(region);
      }
    } catch (e) { /* ignore */ }
  });
})();
